/**
 * Project profitability, and an audit trail for what was charged to each
 * project (boss's request, 26 Sep 2026). Expand a project to see every AP
 * charge with the reason it was flagged, so an over-charge can be traced to
 * the entry that caused it.
 */
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { api, getAccessToken } from '../../lib/api';
import { peso, percent, shortDate } from '../../lib/format';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

type CostFlag =
  | 'PUSHED_OVER_BUDGET'
  | 'AFTER_COMPLETION'
  | 'POSSIBLE_DUPLICATE'
  | 'NO_DOCUMENTS'
  | 'NO_REFERENCE'
  | 'LARGE_SHARE';

interface Charge {
  id: string;
  code: string;
  payee: string;
  description: string | null;
  referenceNo: string | null;
  amount: string;
  status: string;
  entryDate: string;
  paidDate: string | null;
  attachmentCount: number;
  runningTotal: string;
  shareOfCostPct: string;
  flags: CostFlag[];
}

interface ProjectRow {
  projectId: string;
  code: string;
  name: string;
  status: string;
  customerName: string;
  salesOrderCode: string;
  contractVatEx: string;
  contractTotal: string;
  billed: string;
  collected: string;
  uncollected: string;
  actualCostPaid: string;
  actualCostPending: string;
  commission: string;
  totalCost: string;
  estimatedCost: string;
  costVariance: string;
  overBudget: boolean;
  plannedNetProfit: string;
  plannedMarginPct: string;
  actualNetProfit: string;
  actualMarginPct: string;
  cashNetProfit: string;
  costs: Charge[];
  flaggedCount: number;
}

interface Report {
  generatedBy: string;
  generatedAt: string;
  flagLabels: Record<CostFlag, string>;
  projects: ProjectRow[];
  totals: {
    projects: number;
    contractVatEx: string;
    collected: string;
    actualCostPaid: string;
    actualCostPending: string;
    commission: string;
    actualNetProfit: string;
    overBudgetCount: number;
    flaggedCount: number;
  };
}

interface CustomerOption {
  id: string;
  name: string;
}

/** A flag that points at money is louder than one about paperwork. */
const FLAG_TONE: Record<CostFlag, 'critical' | 'warning'> = {
  PUSHED_OVER_BUDGET: 'critical',
  POSSIBLE_DUPLICATE: 'critical',
  AFTER_COMPLETION: 'warning',
  NO_DOCUMENTS: 'warning',
  NO_REFERENCE: 'warning',
  LARGE_SHARE: 'warning',
};

function Stat({
  label,
  value,
  hint,
  dark,
}: {
  label: string;
  value: string;
  hint?: string;
  dark?: boolean;
}): JSX.Element {
  return (
    <Card className={dark ? 'bg-navy text-white dark:bg-navy' : undefined}>
      <p
        className={
          dark
            ? 'text-xs uppercase tracking-wide text-white/70'
            : 'text-xs uppercase tracking-wide text-slate-500'
        }
      >
        {label}
      </p>
      <p className="tabular text-lg font-bold">{value}</p>
      {hint && (
        <p className={dark ? 'text-xs text-white/70' : 'text-xs text-slate-500'}>{hint}</p>
      )}
    </Card>
  );
}

export function ProfitabilityPage(): JSX.Element {
  const [filters, setFilters] = useState({
    customerId: '',
    status: '',
    from: '',
    to: '',
    flaggedOnly: false,
  });
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const params = new URLSearchParams();
  if (filters.customerId) params.set('customerId', filters.customerId);
  if (filters.status) params.set('status', filters.status);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.flaggedOnly) params.set('flaggedOnly', 'true');

  const report = useQuery({
    queryKey: ['profitability', params.toString()],
    queryFn: () => api<{ data: Report }>(`/reports/profitability?${params.toString()}`),
  });
  const customers = useQuery({
    queryKey: ['customers', 'options'],
    queryFn: () => api<{ data: CustomerOption[] }>('/customers?pageSize=200'),
  });

  const exportExcel = async (): Promise<void> => {
    const response = await fetch(`/api/v1/reports/profitability/export?${params.toString()}`, {
      credentials: 'include',
      headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ITCORE-SF_Profitability_${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const data = report.data?.data;
  const totals = data?.totals;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">Profitability</h1>
          <p className="text-sm text-slate-500">
            Contract against every peso charged to it — supplier and expense payables plus
            commission — and what was actually collected.
          </p>
        </div>
        <Button variant="secondary" onClick={() => void exportExcel()}>
          <Download aria-hidden="true" className="h-4 w-4" /> Export Excel
        </Button>
      </header>

      <Card>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Customer">
            <select
              className={inputClass}
              value={filters.customerId}
              onChange={(event) => setFilters({ ...filters, customerId: event.target.value })}
            >
              <option value="">All customers</option>
              {customers.data?.data.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Project status">
            <select
              className={inputClass}
              value={filters.status}
              onChange={(event) => setFilters({ ...filters, status: event.target.value })}
            >
              <option value="">Any status</option>
              <option value="NOT_STARTED">Not started</option>
              <option value="ONGOING">Ongoing</option>
              <option value="ON_HOLD">On hold</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </Field>
          <Field label="Confirmed from" hint="By sales order confirmation date">
            <input
              className={inputClass}
              type="date"
              value={filters.from}
              onChange={(event) => setFilters({ ...filters, from: event.target.value })}
            />
          </Field>
          <Field label="Confirmed to">
            <input
              className={inputClass}
              type="date"
              value={filters.to}
              onChange={(event) => setFilters({ ...filters, to: event.target.value })}
            />
          </Field>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
            type="checkbox"
            checked={filters.flaggedOnly}
            onChange={(event) => setFilters({ ...filters, flaggedOnly: event.target.checked })}
          />
          Only projects over budget or with charges worth checking
        </label>
      </Card>

      {report.isLoading && <Skeleton className="h-64 w-full" />}

      {data && data.projects.length === 0 && (
        <Card>
          <EmptyState
            title="No projects to report on"
            description="Widen the filters, or confirm a lead into a sales order first."
          />
        </Card>
      )}

      {data && totals && data.projects.length > 0 && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label="Contract (VAT-exclusive)"
              value={peso(totals.contractVatEx)}
              hint={`${totals.projects} project${totals.projects === 1 ? '' : 's'}`}
            />
            <Stat
              label="Total project cost"
              value={peso(
                (
                  Number(totals.actualCostPaid) +
                  Number(totals.actualCostPending) +
                  Number(totals.commission)
                ).toFixed(2),
              )}
              hint={`${peso(totals.actualCostPaid)} paid · ${peso(totals.actualCostPending)} pending · ${peso(totals.commission)} commission`}
            />
            <Stat
              label="Total collection"
              value={peso(totals.collected)}
              hint="Confirmed payments, including tax withheld"
            />
            <Stat label="Net profit" value={peso(totals.actualNetProfit)} dark />
          </div>

          {(totals.overBudgetCount > 0 || totals.flaggedCount > 0) && (
            <Card className="border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40">
              <div className="flex items-start gap-3">
                <AlertTriangle
                  aria-hidden="true"
                  className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-300"
                />
                <p className="text-sm text-amber-900 dark:text-amber-100">
                  {totals.overBudgetCount} project
                  {totals.overBudgetCount === 1 ? ' is' : 's are'} over the cost estimated when the
                  deal was sold, and {totals.flaggedCount} charge
                  {totals.flaggedCount === 1 ? '' : 's'} need
                  {totals.flaggedCount === 1 ? 's' : ''} a second look. Open a project below to see
                  which entries and why.
                </p>
              </div>
            </Card>
          )}

          <Card
            title="By project"
            description="Planned profit is the estimate the deal was priced on; actual profit uses every charge booked since."
          >
            <TableWrap>
              <thead>
                <tr>
                  <Th>Project</Th>
                  <Th align="right">Contract</Th>
                  <Th align="right">Estimated cost</Th>
                  <Th align="right">Charged</Th>
                  <Th align="right">Commission</Th>
                  <Th align="right">Total cost</Th>
                  <Th align="right">Collected</Th>
                  <Th align="right">Net profit</Th>
                  <Th>Checks</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {data.projects.map((row) => {
                  const charged = (
                    Number(row.actualCostPaid) + Number(row.actualCostPending)
                  ).toFixed(2);
                  const isOpen = open[row.projectId] === true;
                  return (
                    <Fragment key={row.projectId}>
                      <tr>
                        <Td>
                          <button
                            className="flex items-start gap-1.5 text-left"
                            type="button"
                            aria-expanded={isOpen}
                            onClick={() =>
                              setOpen({ ...open, [row.projectId]: !isOpen })
                            }
                          >
                            {isOpen ? (
                              <ChevronDown aria-hidden="true" className="mt-0.5 h-4 w-4" />
                            ) : (
                              <ChevronRight aria-hidden="true" className="mt-0.5 h-4 w-4" />
                            )}
                            <span>
                              <span className="font-semibold">{row.name}</span>
                              <span className="block font-mono text-xs text-slate-500">
                                {row.code} · {row.salesOrderCode}
                              </span>
                              <span className="block text-xs text-slate-500">
                                {row.customerName}
                              </span>
                            </span>
                          </button>
                        </Td>
                        <Td align="right">{peso(row.contractVatEx)}</Td>
                        <Td align="right">{peso(row.estimatedCost)}</Td>
                        <Td align="right">
                          {peso(charged)}
                          {Number(row.actualCostPending) > 0 && (
                            <span className="block text-xs text-slate-500">
                              {peso(row.actualCostPending)} still pending
                            </span>
                          )}
                        </Td>
                        <Td align="right">{peso(row.commission)}</Td>
                        <Td align="right">
                          {peso(row.totalCost)}
                          {row.overBudget && (
                            <span className="block text-xs font-semibold text-red-600 dark:text-red-300">
                              {peso(row.costVariance)} over estimate
                            </span>
                          )}
                        </Td>
                        <Td align="right">
                          {peso(row.collected)}
                          {Number(row.uncollected) > 0 && (
                            <span className="block text-xs text-slate-500">
                              {peso(row.uncollected)} unpaid
                            </span>
                          )}
                        </Td>
                        <Td align="right">
                          <span
                            className={
                              Number(row.actualNetProfit) < 0
                                ? 'font-semibold text-red-600 dark:text-red-300'
                                : 'font-semibold'
                            }
                          >
                            {peso(row.actualNetProfit)}
                          </span>
                          <span className="block text-xs text-slate-500">
                            {percent(row.actualMarginPct)} · planned {peso(row.plannedNetProfit)}
                          </span>
                        </Td>
                        <Td>
                          {row.flaggedCount === 0 ? (
                            <StatusChip tone="ok">Clean</StatusChip>
                          ) : (
                            <StatusChip tone={row.overBudget ? 'critical' : 'warning'}>
                              {row.flaggedCount} to check
                            </StatusChip>
                          )}
                        </Td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td className="bg-slate-50 px-4 py-4 dark:bg-slate-900/60" colSpan={9}>
                            {row.costs.length === 0 ? (
                              <p className="text-sm text-slate-500">
                                Nothing has been charged to this project yet.
                              </p>
                            ) : (
                              <div className="space-y-3">
                                <p className="text-xs uppercase tracking-wide text-slate-500">
                                  Charges in date order · running total shows when the estimate was
                                  crossed
                                </p>
                                <TableWrap>
                                  <thead>
                                    <tr>
                                      <Th>Entry</Th>
                                      <Th>Payee</Th>
                                      <Th>Reference</Th>
                                      <Th>Status</Th>
                                      <Th align="right">Amount</Th>
                                      <Th align="right">Running total</Th>
                                      <Th align="right">Share</Th>
                                      <Th>Worth checking</Th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                    {row.costs.map((cost) => (
                                      <tr key={cost.id}>
                                        <Td>
                                          <p className="font-mono text-xs">{cost.code}</p>
                                          <p className="text-xs text-slate-500">
                                            {shortDate(cost.entryDate)}
                                          </p>
                                        </Td>
                                        <Td>
                                          <p>{cost.payee}</p>
                                          {cost.description && (
                                            <p className="text-xs text-slate-500">
                                              {cost.description}
                                            </p>
                                          )}
                                        </Td>
                                        <Td>
                                          <p className="text-xs">
                                            {cost.referenceNo ?? '—'}
                                          </p>
                                          <p className="text-xs text-slate-500">
                                            {cost.attachmentCount} document
                                            {cost.attachmentCount === 1 ? '' : 's'}
                                          </p>
                                        </Td>
                                        <Td>
                                          <StatusChip
                                            tone={cost.status === 'COMPLETED' ? 'ok' : 'neutral'}
                                          >
                                            {cost.status === 'COMPLETED' ? 'Paid' : 'Pending'}
                                          </StatusChip>
                                        </Td>
                                        <Td align="right">{peso(cost.amount)}</Td>
                                        <Td align="right">
                                          <span
                                            className={
                                              Number(cost.runningTotal) >
                                              Number(row.estimatedCost)
                                                ? 'text-red-600 dark:text-red-300'
                                                : undefined
                                            }
                                          >
                                            {peso(cost.runningTotal)}
                                          </span>
                                        </Td>
                                        <Td align="right">{percent(cost.shareOfCostPct)}</Td>
                                        <Td>
                                          {cost.flags.length === 0 ? (
                                            <span className="text-xs text-slate-400">—</span>
                                          ) : (
                                            <span className="flex flex-wrap gap-1">
                                              {cost.flags.map((flag) => (
                                                <StatusChip key={flag} tone={FLAG_TONE[flag]}>
                                                  {data.flagLabels[flag]}
                                                </StatusChip>
                                              ))}
                                            </span>
                                          )}
                                        </Td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </TableWrap>
                                <p className="text-xs text-slate-500">
                                  Cash view: {peso(row.collected)} collected less{' '}
                                  {peso(row.actualCostPaid)} paid out and {peso(row.commission)}{' '}
                                  commission — {peso(row.cashNetProfit)} in hand.
                                </p>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </TableWrap>
          </Card>

          <p className="text-xs text-slate-500">
            Generated by {data.generatedBy} · disapproved and cancelled payables are excluded.
          </p>
        </>
      )}
    </div>
  );
}
